<script setup lang="ts">
import { getTrail } from '~/utils/cursorTrails'
import type { TrailContext } from '~/utils/cursorTrails/types'

const props = defineProps<{
  trail: string
  mouseX: number
  mouseY: number
  mouseActive: boolean
  primaryColor?: string
}>()

const canvasRef = ref<HTMLCanvasElement | null>(null)
let animationId: number | null = null
let state: unknown = null
let startTime = 0
let lastTime = 0

function getContext(): TrailContext | null {
  const canvas = canvasRef.value
  if (!canvas) return null
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  const dpr = window.devicePixelRatio || 1
  return {
    canvas,
    ctx,
    width: canvas.clientWidth,
    height: canvas.clientHeight,
    dpr,
    time: 0,
    deltaTime: 0,
    mouseX: props.mouseX,
    mouseY: props.mouseY,
    mouseActive: props.mouseActive,
    primaryColor: props.primaryColor || '#6366f1'
  }
}

function resizeCanvas() {
  const canvas = canvasRef.value
  if (!canvas) return
  const dpr = window.devicePixelRatio || 1
  const rect = canvas.getBoundingClientRect()
  canvas.width = rect.width * dpr
  canvas.height = rect.height * dpr
  const ctx = canvas.getContext('2d')
  if (ctx) ctx.scale(dpr, dpr)

  initEffect()
}

function initEffect() {
  const trailDef = getTrail(props.trail)
  const ctx = getContext()
  if (!trailDef || !ctx) {
    state = null
    return
  }
  state = trailDef.init(ctx)
  startTime = performance.now()
  lastTime = startTime
}

function loop(now: number) {
  const trailDef = getTrail(props.trail)
  const canvas = canvasRef.value
  if (!trailDef || !canvas || !state) {
    animationId = null
    return
  }

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  const dpr = window.devicePixelRatio || 1
  const width = canvas.clientWidth
  const height = canvas.clientHeight

  const time = now - startTime
  const deltaTime = Math.min(now - lastTime, 50)
  lastTime = now

  const trailCtx: TrailContext = {
    canvas,
    ctx,
    width,
    height,
    dpr,
    time,
    deltaTime,
    mouseX: props.mouseX,
    mouseY: props.mouseY,
    mouseActive: props.mouseActive,
    primaryColor: props.primaryColor || '#6366f1'
  }

  ctx.clearRect(0, 0, width, height)
  trailDef.update(state, trailCtx)
  trailDef.draw(state, trailCtx)

  animationId = requestAnimationFrame(loop)
}

function startLoop() {
  stopLoop()
  if (props.trail && getTrail(props.trail)) {
    animationId = requestAnimationFrame(loop)
  }
}

function stopLoop() {
  if (animationId !== null) {
    cancelAnimationFrame(animationId)
    animationId = null
  }
}

function clearCanvas() {
  const canvas = canvasRef.value
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  if (ctx) ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight)
}

watch(() => props.trail, () => {
  stopLoop()
  if (!props.trail || props.trail === 'none') {
    state = null
    clearCanvas()
    return
  }
  initEffect()
  startLoop()
})

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  resizeCanvas()

  resizeObserver = new ResizeObserver(() => {
    resizeCanvas()
  })
  if (canvasRef.value) {
    resizeObserver.observe(canvasRef.value)
  }

  if (props.trail && props.trail !== 'none') {
    startLoop()
  }
})

onUnmounted(() => {
  stopLoop()
  resizeObserver?.disconnect()
})
</script>

<template>
  <canvas
    v-show="trail && trail !== 'none'"
    ref="canvasRef"
    class="fixed inset-0 w-full h-full pointer-events-none z-9998"
  />
</template>
